import { memo, useRef, useState } from 'react';
import { Button } from '@clay-run/glaze/button';
import { styles as buttonStyles, type ButtonVariant } from '@clay-run/glaze/button';
import type { TerraSignalRow } from '@shared/schemas/terra-signals';
import type { ApiProblemError } from '../../../lib/apiProblemError';
import { getSignalRowActions, type SignalRowActionsInput } from './rowActions';
import { useUnsnoozeSignal } from './useSignalsMutations';
import { SnoozePopover } from './SnoozePopover';
import { useGongOpenLink } from './useGongOpenLink';

/**
 * The Signals-tab action bar (plan 2026-09-24-002 U6, bead .16) — the ONE
 * render of `getSignalRowActions(row, now)`'s button matrix (D14), shared
 * verbatim by the table's sticky `rowAction` slot (`layout="row"`), the
 * Timeline affordance line inside the expanded card (`layout="compact"`),
 * and the Signal card (`layout="card"`, QA batch 2 unit B8, D-I). Never a
 * second copy of the buttons — the Signal card reads the same matrix only
 * for its chip and archive prefill.
 *
 * Button order and height (Spencer's override on qa-batch6-plan.md's D1/D3
 * — no separate Done button; QA batch 2 unit B7's D-G superseded): `[Verb]
 * [Mark as read] [Archive]`, at most three buttons, the verb anchor first.
 * "Mark as read" is the SAME button as the old "Snooze" (identical
 * `SnoozePopover`/`useSnoozeSignal` wiring, only the visible label and a
 * `title` tooltip changed — "Hide this task until you choose, or until Gong
 * moves to the next step. It doesn't complete the step in Gong."); the
 * popover's own "Indefinitely" option (gated on `hasSnoozeStepSignature`,
 * `rowActions.ts`) is what used to be a standalone Done button. "Bring
 * back" takes its slot when snoozed (the two are mutually exclusive per
 * `getSignalRowActions`'s state table). Every layout renders them on ONE
 * line by default — the card layout's old
 * "verb on its own line above `[Mark as read] [Archive]`" (D-I) is
 * superseded by qa-batch5-plan.md D-B (the card's buttons now sit in its
 * header row); `card` differs from `row` only in carrying `aria-expanded`
 * on Archive AND (fix-plan-batch5.md U2/D2) wrapping onto a second line,
 * right-aligned, when the header cell is too narrow for one line — `row`
 * (the sticky action cell, `whitespace-nowrap`) and `compact` (Timeline)
 * keep their exact one-line class strings, byte-for-byte, since wrapping
 * there would collapse the table column. The verb anchor's classes are
 * built from the SAME Glaze `Button` base + size tokens the "Mark as
 * read"/Archive `<Button>`s render with (`glazeAnchorClassName`), so the
 * three are the same height by
 * construction — see `__tests__/SignalRowActions.test.tsx`'s class-superset
 * assertion.
 *
 * Archive never opens a dialog (D-I): `onArchiveRequest` fires instead —
 * a row/Timeline click routes (through `useSignalsUiStore.requestArchive`)
 * to the Signal card's inline archive form; in the card itself it toggles
 * that form.
 */

/** Tooltip on every write button while a Flow admin is viewing as a rep (Signals V3 PR 3, U15). */
export const READ_ONLY_TITLE = 'Read-only while viewing as a rep';

export interface SignalRowActionsRow
  extends SignalRowActionsInput,
    Pick<TerraSignalRow, 'signal_id' | 'signal_type' | 'contact_id' | 'account_id' | 'signal_instance_id' | 'step_number'> {}

/** Row (table's sticky action slot), Timeline (`compact`), or Signal card header (`card`: the row's one line plus `aria-expanded` on Archive, qa-batch5 D-B) — replaces the old boolean `compact` (D-G). */
export type SignalRowActionsLayout = 'row' | 'compact' | 'card';

export interface SignalRowActionsProps {
  row: SignalRowActionsRow;
  /** Fires once a Snooze/Bring back/Archive mutation completes — drives auto-expand-next. */
  onActed: (signalId: string) => void;
  /**
   * The verification-freshness clock passed to `getSignalRowActions` (D-A)
   * — every caller passes its shared minute-token clock (`SignalsFlowTable`
   * via `signalsTableConfig.tsx`, `ContactTimeline.tsx`, `SignalActionCard`).
   */
  now: Date;
  /**
   * Fires when Archive is clicked, with the row's signal id — opens (row,
   * Timeline) or toggles (card) the Signal card's inline archive form (D-I).
   */
  onArchiveRequest?: (signalId: string) => void;
  /** Card layout only — whether the inline archive form Archive controls is open (`aria-expanded`). */
  archiveExpanded?: boolean;
  /** Timeline affordance line / Signal card rendering — smaller buttons, tighter gaps. Replaces the old boolean `compact` (D-G). */
  layout?: SignalRowActionsLayout;
  /**
   * View as is read-only (U15): every Terra write button (Mark as read, Bring back, Archive) is
   * disabled with `READ_ONLY_TITLE`. The verb anchor only opens Gong, so it stays usable.
   */
  disabled?: boolean;
}

type ActivePopover = 'snooze' | null;

/**
 * Composes Glaze's own `Button` style tokens for a REAL `<a>` element (never
 * `ButtonLink`/`Link` — see the verb anchor's own doc comment for why) —
 * Clay semantic tokens only, no hardcoded hex/raw Tailwind palette.
 */
export function glazeAnchorClassName(variant: ButtonVariant, size: keyof typeof buttonStyles.sizes): string {
  const variantClasses = variant === 'ghost' ? buttonStyles.ghost : [...buttonStyles.solid, ...buttonStyles.colors[variant]];
  return [...buttonStyles.base, ...buttonStyles.sizes[size], ...variantClasses].join(' ');
}

/**
 * R14 — "Bring back" is the one action with no popover of its own, so its
 * refusal has nowhere else to go. Same danger-text treatment
 * SnoozePopover/the archive form use, so the three read identically.
 * `whitespace-normal` undoes the sticky action cell's own `whitespace-nowrap`
 * (FlowTable.tsx:1466) — a full sentence must wrap rather than stretch the
 * table. Shared with `SignalResponseRowActions`.
 */
export function BringBackError({ error, compact = false, align = 'left' }: { error: ApiProblemError; compact?: boolean; align?: 'left' | 'right' }) {
  return (
    <div
      data-testid="bring-back-error"
      className={`max-w-[220px] whitespace-normal text-content-danger ${compact ? 'text-[11px]' : 'text-[11.5px]'} ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {/* `.title` is optional on `ApiProblemError` (unset for a network/
          aborted failure) — fall back to `.message`, a rep-safe sentence
          either way (plan U4a). */}
      <div className="font-medium">{error.title ?? error.message}</div>
      {error.detail && <div>{error.detail}</div>}
      {error.oauthUrl && (
        <a href={error.oauthUrl} className="font-semibold underline">
          Reconnect Salesforce
        </a>
      )}
    </div>
  );
}

/**
 * Memoized (bead terra-design-v2-table-tweaks-at4.20, Stage 4 perf budget,
 * mitigation 3): `LedgerTable`'s own `LedgerRow` memo (`LedgerTable.tsx:938`)
 * only bails when EVERY prop it forwards — including `config`, shared by
 * every row — is unchanged, so a `config` rebuild for a reason unrelated to
 * any one row (e.g. `handleActed`/`handleArchiveRequest` changing identity
 * off an unrelated `rows` refetch) still re-runs every `LedgerRowImpl`, which
 * unconditionally calls `config.rowAction(row)`. Wrapping the actual button
 * matrix in `memo` stops THAT re-render from cascading into the button
 * markup/`getSignalRowActions` computation for a row whose own `row`/`now`/
 * `onActed`/`onArchiveRequest`/`archiveExpanded`/`layout` props are all
 * unchanged — safe by construction (a pure prop-driven presentational
 * component, no internally-mutated ref an unequal-but-stale render would
 * miss) and behaviour-neutral for every existing caller (row, Timeline,
 * Signal card alike). It does NOT, on its own, avoid a re-render on every
 * `now` tick (every row's `now` prop genuinely changes together each
 * minute) — see `useSignalTableModel.ts`'s own doc note on the remaining,
 * out-of-scope budget gap.
 */
function SignalRowActionsImpl({ row, onActed, now, onArchiveRequest, archiveExpanded, layout = 'row', disabled = false }: SignalRowActionsProps) {
  const actions = getSignalRowActions(row, now);
  // `onActed` rides the HOOK's own `onSuccess` (round-1 fix D-J), never a
  // per-`mutate()` callback: the list refetch this mutation triggers
  // unmounts this very row, and TanStack v5 skips per-mutate callbacks once
  // the calling observer is gone — which is exactly when auto-expand-next
  // was silently doing nothing.
  const unsnooze = useUnsnoozeSignal({ onActed });
  const gongLink = useGongOpenLink(row);
  const [activePopover, setActivePopover] = useState<ActivePopover>(null);
  const snoozeButtonRef = useRef<HTMLButtonElement>(null);

  const compact = layout === 'compact';
  const size = compact ? 'xs' : 'sm';
  const gapClass = compact ? 'gap-1' : 'gap-1.5';

  const hasAnything = actions.showArchive || actions.showSnooze || actions.showBringBack || !!actions.verb;
  if (!hasAnything) return null;

  const card = layout === 'card';

  const verb = actions.verb ? (
    <a
      href={gongLink.href}
      target="_blank"
      rel="noopener noreferrer"
      className={glazeAnchorClassName('primary', size)}
      onClick={gongLink.onClick}
    >
      {actions.verb.label}
    </a>
  ) : null;

  const secondaryButtons = (
    <>
      {actions.showSnooze && (
        <>
          <Button
            ref={snoozeButtonRef}
            type="button"
            size={size}
            variant="secondary"
            disabled={disabled}
            title={disabled ? READ_ONLY_TITLE : "Hide this task until you choose, or until Gong moves to the next step. It doesn't complete the step in Gong."}
            onClick={() => {
              if (!disabled) setActivePopover('snooze');
            }}
          >
            Mark as read
          </Button>
          {activePopover === 'snooze' && (
            <SnoozePopover
              row={row}
              anchorRef={snoozeButtonRef}
              onClose={() => setActivePopover(null)}
              onActed={onActed}
            />
          )}
        </>
      )}

      {actions.showBringBack && (
        <Button
          type="button"
          size={size}
          variant="secondary"
          loading={unsnooze.isPending}
          disabled={disabled}
          title={disabled ? READ_ONLY_TITLE : undefined}
          onClick={() => {
            if (!disabled) unsnooze.mutate({ signalId: row.signal_id });
          }}
        >
          Bring back
        </Button>
      )}

      {actions.showArchive && (
        <Button
          type="button"
          size={size}
          variant="danger-secondary"
          aria-expanded={card ? !!archiveExpanded : undefined}
          disabled={disabled}
          title={disabled ? READ_ONLY_TITLE : undefined}
          onClick={() => {
            if (!disabled) onArchiveRequest?.(row.signal_id);
          }}
        >
          Archive
        </Button>
      )}
    </>
  );

  return (
    <div
      className={`inline-flex flex-col gap-0.5 ${card ? 'items-end' : 'items-start'}${card ? ' min-w-0 max-w-full' : ''}`}
      onClick={e => e.stopPropagation()}
    >
      <div className={card ? `flex min-w-0 flex-wrap items-center justify-end ${gapClass} gap-y-1` : `inline-flex items-center ${gapClass}`}>
        {verb}
        {secondaryButtons}
      </div>

      {/* R14 — "Bring back" is the one action with no popover of its own, so
          its refusal has nowhere else to go. Same danger-text treatment
          SnoozePopover/the archive form use, so the three read identically.
          `whitespace-normal` undoes the sticky action cell's own
          `whitespace-nowrap` (FlowTable.tsx:1466) — a full sentence must
          wrap rather than stretch the table. */}
      {actions.showBringBack && unsnooze.error && <BringBackError error={unsnooze.error} compact={compact} align={card ? 'right' : 'left'} />}
    </div>
  );
}

export const SignalRowActions = memo(SignalRowActionsImpl);
